import { useEffect, useMemo, useState } from "react";
import { ScanLine, Search } from "lucide-react";
import { EmptyState } from "../components/ui/EmptyState";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { DASH, formatDay } from "../lib/format";

/**
 * P3/D7 — traçabilité TRÈS légère : le portail n'affiche que le numéro de lot et le
 * produit auquel il se rattache, avec la livraison qui l'a apporté. Rien d'autre ne
 * transite (ni ingrédient, ni fournisseur, ni process, ni relevé HACCP) : le reste
 * s'obtient auprès de l'artisan, comme le rappelle la note de bas de page.
 *
 * W7-B — trois situations se ressemblaient à l'écran : rien n'a été livré, la
 * recherche ne trouve rien, la lecture a échoué. Elles sont désormais distinctes,
 * et le plafond de 200 lignes imposé par l'API est DIT (une traçabilité tronquée
 * en silence est pire que pas de traçabilité).
 */
interface LotRow {
  lotCode: string;
  productName: string;
  deliveredAt: string;
  blNumber: string | null;
}

/** Plafond serveur de `/me/tracabilite` (portal-auth.routes.ts). */
const LOTS_CAP = 200;

export function TraceabilityPage() {
  const { data, status, error, reload } = usePortalResource<LotRow[]>(() =>
    apiGet<LotRow[]>("/me/tracabilite"),
  );
  const [query, setQuery] = useState("");

  const rows = useMemo(() => data ?? [], [data]);

  // La liste est plafonnée côté API : le filtre reste local, sans aller-retour
  // réseau à chaque frappe.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.lotCode.toLowerCase().includes(q) || r.productName.toLowerCase().includes(q),
    );
  }, [rows, query]);

  useEffect(() => {
    // Une nouvelle lecture repart d'une recherche vierge : sans cela, un filtre
    // resté en place ferait croire à une liste vide après un « Réessayer ».
    if (status === "loading") setQuery("");
  }, [status]);

  return (
    <main className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader
        title="Traçabilité"
        subtitle="Les numéros de lot des produits qui vous ont été livrés."
      />

      <PortalState status={status} label="vos lots livrés" error={error} onRetry={reload} />

      {status === "ready" && rows.length > 0 && (
        <label className="relative block mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un produit ou un lot"
            aria-label="Rechercher un produit ou un lot"
            className="w-full rounded-xl2 border border-border bg-card py-2.5 pl-9 pr-3 text-sm text-ink placeholder:text-muted focus:border-terracotta focus:outline-none"
          />
        </label>
      )}

      {status === "ready" && rows.length === 0 && (
        <EmptyState
          icon={ScanLine}
          title="Aucun lot livré pour l'instant"
          description="Les numéros de lot apparaissent ici dès qu'un bon de livraison signé vous est rattaché."
          action={{ to: "/delivery-notes", label: "Voir mes bons de livraison" }}
        />
      )}

      {status === "ready" && rows.length > 0 && filtered.length === 0 && (
        <p className="text-sm text-muted">Aucun lot ne correspond à « {query.trim()} ».</p>
      )}

      {status === "ready" && filtered.length > 0 && (
        <>
          <div className="bg-card border border-border rounded-xl2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream border-b border-border">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-ink">Lot</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink">Produit</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink">Livré le</th>
                  <th className="text-left px-4 py-3 font-semibold text-ink">Bon de livraison</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={`${r.lotCode}|${r.productName}|${r.blNumber ?? ""}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-4 py-3 font-mono text-ink whitespace-nowrap">{r.lotCode}</td>
                    <td className="px-4 py-3 text-ink">{r.productName}</td>
                    <td className="px-4 py-3 text-muted whitespace-nowrap">
                      {formatDay(r.deliveredAt)}
                    </td>
                    <td className="px-4 py-3 text-muted whitespace-nowrap">{r.blNumber ?? DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ListCapNotice shown={rows.length} cap={LOTS_CAP} label="lots" genre="m" />
        </>
      )}

      <p className="mt-4 text-xs text-muted">
        Informations complètes disponibles auprès de votre fournisseur sur demande.
      </p>
    </main>
  );
}
